import { useQuery } from '@tanstack/react-query'
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
} from 'firebase/firestore'
import { db, AGENT_ID } from '@/lib/firebase'
import type {
  AgentEpisodeMetrics,
  ModeratorEpisodeMetrics,
  MetricsSummary,
  SangamSession,
  ContentAnalysis,
} from '@/types'

// Helper to convert Firestore timestamp
const toDate = (timestamp: Timestamp | Date | undefined): Date => {
  if (!timestamp) return new Date()
  if (timestamp instanceof Timestamp) return timestamp.toDate()
  return timestamp
}

/**
 * Fetch all recorded sessions that have metrics.
 */
export function useMetricsSessions() {
  return useQuery({
    queryKey: ['metrics-sessions'],
    queryFn: async (): Promise<(SangamSession & { metrics_summary?: MetricsSummary })[]> => {
      const sangamRef = collection(db, 'agents', AGENT_ID, 'sangam')
      const q = query(
        sangamRef,
        where('status', 'in', ['RECORDED', 'COMPLETED']),
        orderBy('created_at', 'desc'),
        limit(20)
      )
      const snapshot = await getDocs(q)
      return snapshot.docs.map((d) => {
        const data = d.data()
        return {
          id: d.id,
          topic: data.topic || '',
          status: data.status,
          active_speaker: data.active_speaker,
          turn_number: data.turn_number || 0,
          participants_count: data.participants_count || 0,
          created_at: toDate(data.created_at),
          scheduled_at: data.scheduled_at ? toDate(data.scheduled_at) : undefined,
          started_at: data.started_at ? toDate(data.started_at) : undefined,
          queue: data.queue || [],
          metrics_summary: data.metrics_summary || undefined,
        } as SangamSession & { metrics_summary?: MetricsSummary }
      })
    },
    refetchInterval: 30000,
  })
}

/**
 * Fetch moderator metrics for a specific session.
 */
export function useModeratorMetrics(sessionId: string | null) {
  return useQuery({
    queryKey: ['moderator-metrics', sessionId],
    queryFn: async (): Promise<ModeratorEpisodeMetrics | null> => {
      if (!sessionId) return null
      const docRef = doc(
        db,
        'agents',
        AGENT_ID,
        'sangam',
        sessionId,
        'metrics',
        'moderator'
      )
      const docSnap = await getDoc(docRef)
      if (!docSnap.exists()) return null
      return docSnap.data() as ModeratorEpisodeMetrics
    },
    enabled: !!sessionId,
  })
}

/**
 * Fetch all agent metrics for a specific session.
 */
export function useAgentMetrics(sessionId: string | null) {
  return useQuery({
    queryKey: ['agent-metrics', sessionId],
    queryFn: async (): Promise<AgentEpisodeMetrics[]> => {
      if (!sessionId) return []
      const metricsRef = collection(
        db,
        'agents',
        AGENT_ID,
        'sangam',
        sessionId,
        'metrics'
      )
      const EXCLUDED_DOCS = new Set(['moderator', 'content_analysis'])
      const snapshot = await getDocs(metricsRef)
      return snapshot.docs
        .filter((d) => !EXCLUDED_DOCS.has(d.id))
        .map((d) => {
          const raw = d.data()
          // Provide safe defaults for any missing nested objects
          return {
            agent_name: raw.agent_name ?? d.id,
            session_id: raw.session_id ?? sessionId,
            collected_at: raw.collected_at ?? '',
            episode_duration_seconds: raw.episode_duration_seconds ?? 0,
            floor: {
              claims_attempted: 0, claims_won: 0, claims_lost: 0, claims_error: 0,
              claim_success_rate: 0, avg_hold_time_seconds: 0, hold_times: [],
              releases_with_post: 0, releases_without_post: 0,
              retries_scheduled: 0, retries_executed: 0, retries_succeeded: 0,
              recovery_signals_received: 0, recovery_claims_attempted: 0,
              ...raw.floor,
            },
            urgency: {
              total_computations: 0, avg_urgency: 0, above_threshold_rate: 0, scores: [],
              ...raw.urgency,
            },
            validation: {
              total_attempts: 0, passes: 0, failures: 0, pass_rate: 0,
              failure_types: {}, retries_per_turn: [],
              ...raw.validation,
            },
            generation: {
              total_calls: 0, avg_time_seconds: 0, total_time_seconds: 0, times: [],
              ...raw.generation,
            },
            participation: {
              turns_posted: 0, turns_by_phase: {}, silence_durations: [],
              invitation_response_times: [],
              ...raw.participation,
            },
          } as AgentEpisodeMetrics
        })
    },
    enabled: !!sessionId,
  })
}

/**
 * Fetch LLM-generated content analysis for a specific session.
 */
export function useContentAnalysis(sessionId: string | null) {
  return useQuery({
    queryKey: ['content-analysis', sessionId],
    queryFn: async (): Promise<ContentAnalysis | null> => {
      if (!sessionId) return null
      const docRef = doc(
        db,
        'agents',
        AGENT_ID,
        'sangam',
        sessionId,
        'metrics',
        'content_analysis'
      )
      const docSnap = await getDoc(docRef)
      if (!docSnap.exists()) return null
      return docSnap.data() as ContentAnalysis
    },
    enabled: !!sessionId,
  })
}
